import { useState } from 'react';
import { cropBox, type Box, type MediaRegion } from '@familienbaum/shared';
import { cn } from '@/lib/utils';

// `Box`, `padBox` und `cropBox` liegen in `@familienbaum/shared`: derselbe
// Ausschnitt wird auf dem Server von `sharp` gerechnet, und zwei Fassungen
// derselben Formel driften auseinander, ohne dass es jemand bemerkt.
export { cropBox, padBox, type Box } from '@familienbaum/shared';

/**
 * Zeigt einen Bildbereich als Ausschnitt – ohne serverseitiges Zuschneiden.
 *
 * Das Bild wird auf `1/Breite` der Rahmenbreite vergrößert und um `x` nach
 * links geschoben; dann liegt genau der markierte Bereich im Rahmen. Der
 * Ausschnitt muss dabei in *Pixeln* das Seitenverhältnis des Rahmens haben –
 * nicht in normalisierten Koordinaten, sonst wird bei hochkant aufgenommenen
 * Bildern jedes Gesicht in die Länge gezogen.
 *
 * `aspect` ist Breite/Höhe des Rahmens: 1 für den runden Avatar und die
 * Galerie, 0.8 für ein Porträt im Format 4:5. Bei einem hochkanten Rahmen
 * wächst der Ausschnitt nach oben und unten – bei einem Gesicht heißt das
 * Stirn und Schultern, und genau das will man sehen.
 *
 * Zwei Vorkehrungen gegen Verzerrung:
 *  - der Ausschnitt wird gleichmäßig geschrumpft, wenn er über eine Bildkante
 *    hinausragt. Würde stattdessen jede Seite einzeln auf 1 gekappt, ginge das
 *    Seitenverhältnis dabei verloren,
 *  - das Bild ist ein `<img>` mit `object-fit: cover`. Passt es doch nicht
 *    (Bildmaße unbekannt, extreme Markierung), wird beschnitten statt
 *    gestaucht. Fehlende Maße holt sich die Komponente beim Laden selbst.
 */
export function FaceCrop({
  src,
  region,
  alt,
  className,
  padding = 0.3,
  aspect = 1,
  imageWidth,
  imageHeight,
}: {
  src: string;
  region: Box;
  alt?: string;
  className?: string;
  padding?: number;
  /** Breite/Höhe des Rahmens. 1 = quadratisch, 0.8 = Porträt 4:5. */
  aspect?: number;
  imageWidth?: number | null;
  imageHeight?: number | null;
}) {
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const iw = imageWidth ?? natural?.w ?? null;
  const ih = imageHeight ?? natural?.h ?? null;

  const box = cropBox(region, { padding, aspect, imageWidth: iw, imageHeight: ih });

  return (
    <div className={cn('relative overflow-hidden bg-sunken', className)}>
      <img
        src={src}
        alt={alt ?? ''}
        loading="lazy"
        draggable={false}
        onLoad={(event) => {
          const { naturalWidth, naturalHeight } = event.currentTarget;
          if (!imageWidth || !imageHeight) setNatural({ w: naturalWidth, h: naturalHeight });
        }}
        className="absolute max-w-none object-cover"
        style={{
          width: `${(1 / box.width) * 100}%`,
          height: `${(1 / box.height) * 100}%`,
          left: `${(-box.x / box.width) * 100}%`,
          top: `${(-box.y / box.height) * 100}%`,
        }}
      />
    </div>
  );
}

/** Bequemer Aufruf mit einer Markierung aus der API. */
export function RegionCrop({
  src,
  region,
  className,
  padding,
  aspect,
  imageWidth,
  imageHeight,
}: {
  src: string;
  region: Pick<MediaRegion, 'x' | 'y' | 'width' | 'height' | 'person' | 'label'>;
  className?: string;
  padding?: number;
  aspect?: number;
  imageWidth?: number | null;
  imageHeight?: number | null;
}) {
  return (
    <FaceCrop
      src={src}
      region={region}
      alt={region.person?.displayName ?? region.label ?? 'Markierter Bildbereich'}
      className={className}
      padding={padding}
      aspect={aspect}
      imageWidth={imageWidth}
      imageHeight={imageHeight}
    />
  );
}
